/**
 * An event's sign-ups as a spreadsheet, for the organizer to mail or print.
 * The same quoting as the plan needs list (csvField guards formulas), CRLF
 * line ends, one row per sign-up.
 */
import { csvField } from './planNeedsCsv';
import { formatEventWhen, type EventTimes } from './eventCalendar';
import type { PlanEventRsvp } from '../types';

export const RSVP_CSV_COLUMNS = ['Name', 'Email', 'People', 'Signed up', 'Event', 'When'] as const;

interface EventForCsv extends EventTimes {
  title: string;
}

export function eventRsvpFilename(event: { title: string }, today = new Date()): string {
  const slug =
    event.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-+|-+$)/g, '') || 'event';
  return `REDI-signups-${slug}-${today.toISOString().slice(0, 10)}.csv`;
}

export function buildEventRsvpCsv(event: EventForCsv, rsvps: PlanEventRsvp[]): string {
  const when = formatEventWhen(event);
  const rows: string[] = [RSVP_CSV_COLUMNS.map((c) => csvField(c)).join(',')];
  for (const rsvp of rsvps) {
    rows.push(
      [
        rsvp.name,
        rsvp.email,
        rsvp.partySize,
        rsvp.createdAt.toDate().toISOString().slice(0, 10),
        event.title,
        when,
      ]
        .map((cell) => csvField(cell))
        .join(',')
    );
  }
  return `${rows.join('\r\n')}\r\n`;
}

/** The addresses to paste into a mail client, one per sign-up, comma separated. */
export function rsvpEmailList(rsvps: PlanEventRsvp[]): string {
  const seen = new Set<string>();
  for (const rsvp of rsvps) seen.add(rsvp.email.trim().toLowerCase());
  return [...seen].join(', ');
}

/** Total people: the sum of party sizes, which is what goingCount counts. */
export function rsvpHeadcount(rsvps: PlanEventRsvp[]): number {
  return rsvps.reduce((sum, r) => sum + r.partySize, 0);
}
